// =============================================================================
// design-system/native — reactstrap'in API-uyumlu, NATIVE HTML + .ds-* class
// basan ikamesi. Amaç: reactstrap paketini ve Bootstrap class isimlerini
// tamamen söküp kendi token'lı semantik (.ds-*) sistemimize geçmek.
//
// Codemod sözleşmesi: `import { X } from "@/design-system/native"` →
//                     `import { X } from "@/design-system/native"`
// API kasıtlı olarak reactstrap ile uyumlu (color/size/outline/isOpen/toggle…)
// ki 351 dosyanın migrasyonu büyük ölçüde import-swap olsun.
//
// Render edilen DOM'da Bootstrap class'ı YOK — yalnız .ds-* (styles/ui/*).
// Davranış gereken Modal/Dropdown gerçek (ama minimal) component; gerisi
// ince native sarmalayıcı.
// =============================================================================
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const cx = (...parts) => parts.filter(Boolean).join(" ");

// reactstrap renk adı → .ds-* modifier
const variant = (base, color, outline) => {
  if (!color) return "";
  return outline ? `${base}--outline-${color}` : `${base}--${color}`;
};

// -----------------------------------------------------------------------------
// Button
// -----------------------------------------------------------------------------
export const Button = forwardRef(function Button(
  { color, outline, size, block, active, close, tag: Tag = "button", className, type, children, ...rest },
  ref
) {
  if (close) {
    return (
      <button ref={ref} type={type || "button"} className={cx("ds-btn-close", className)} aria-label="Close" {...rest}>
        {children}
      </button>
    );
  }
  const cls = cx(
    "ds-btn",
    variant("ds-btn", color, outline),
    size === "sm" && "ds-btn--sm",
    size === "lg" && "ds-btn--lg",
    block && "ds-btn--block",
    active && "is-active",
    className
  );
  const extra = Tag === "button" ? { type: type || "button" } : {};
  return (
    <Tag ref={ref} className={cls} {...extra} {...rest}>
      {children}
    </Tag>
  );
});

export const ButtonGroup = forwardRef(function ButtonGroup({ size, vertical, className, ...rest }, ref) {
  return <div ref={ref} role="group" className={cx("ds-btn-group", size === "sm" && "ds-btn-group--sm", size === "lg" && "ds-btn-group--lg", vertical && "ds-btn-group--vertical", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Form primitives (native <input>/<select>/<textarea> + .ds-* class)
// -----------------------------------------------------------------------------
export const Input = forwardRef(function Input(
  { type = "text", tag: Tag, bsSize, valid, invalid, innerRef, className, children, ...rest },
  ref
) {
  const r = innerRef || ref;
  const state = cx(valid && "is-valid", invalid && "is-invalid");
  // reactstrap paritesi: `tag` verilmişse (örn. react-input-mask InputMask) onu
  // render et — yoksa mask/maskChar gibi prop'lar düz <input>'a sızıp hem React
  // uyarısı basıyor hem de maskeleme çalışmıyordu.
  if (Tag) {
    return (
      <Tag
        ref={r}
        type={type}
        className={cx("ds-input", bsSize && `ds-input--${bsSize}`, state, className)}
        {...rest}
      />
    );
  }
  if (type === "select") {
    return (
      <select ref={r} className={cx("ds-select", bsSize && `ds-select--${bsSize}`, state, className)} {...rest}>
        {children}
      </select>
    );
  }
  if (type === "textarea") {
    return <textarea ref={r} className={cx("ds-input", bsSize && `ds-input--${bsSize}`, state, className)} {...rest} />;
  }
  if (type === "checkbox" || type === "radio" || type === "switch") {
    return (
      <input
        ref={r}
        type={type === "switch" ? "checkbox" : type}
        className={cx("ds-check__input", type === "switch" && "ds-switch__input", state, className)}
        {...rest}
      />
    );
  }
  return <input ref={r} type={type} className={cx("ds-input", bsSize && `ds-input--${bsSize}`, state, className)} {...rest} />;
});

export const Label = forwardRef(function Label(
  // reactstrap API uyumu: `for`→htmlFor map; kolon-genişliği prop'larını (sm/md/
  // lg/xl/xs/xxl/widths/size) YUT — DOM <label>'a sızıp React uyarısı basmasınlar.
  { check, for: forProp, htmlFor, sm, md, lg, xl, xs, xxl, widths, size, tag: Tag = "label", className, ...rest },
  ref
) {
  return <Tag ref={ref} htmlFor={htmlFor ?? forProp} className={cx(check ? "ds-check__label" : "ds-label", className)} {...rest} />;
});

export const Form = forwardRef(function Form({ inline, className, ...rest }, ref) {
  return <form ref={ref} className={cx("ds-form", inline && "ds-form--inline", className)} {...rest} />;
});

export const FormGroup = forwardRef(function FormGroup({ check, row, inline, tag: Tag = "div", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-formgroup", check && "ds-check", row && "ds-formgroup--row", inline && "ds-check--inline", className)} {...rest} />;
});

export const FormFeedback = forwardRef(function FormFeedback({ valid, tooltip, className, ...rest }, ref) {
  return <div ref={ref} className={cx(valid ? "ds-valid-feedback" : "ds-invalid-feedback", className)} {...rest} />;
});

export const FormText = forwardRef(function FormText({ className, ...rest }, ref) {
  return <small ref={ref} className={cx("ds-form-hint", className)} {...rest} />;
});

export const InputGroup = forwardRef(function InputGroup({ size, className, ...rest }, ref) {
  return <div ref={ref} className={cx("ds-inputgroup", size && `ds-inputgroup--${size}`, className)} {...rest} />;
});

export const InputGroupText = forwardRef(function InputGroupText({ tag: Tag = "span", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-inputgroup__text", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Table / Badge / Alert / Progress / Spinner
// -----------------------------------------------------------------------------
export const Table = forwardRef(function Table({ size, bordered, borderless, striped, hover, responsive, className, children, ...rest }, ref) {
  const table = (
    <table ref={ref} className={cx("ds-table", size === "sm" && "ds-table--sm", bordered && "ds-table--bordered", borderless && "ds-table--borderless", striped && "ds-table--striped", hover && "ds-table--hover", className)} {...rest}>
      {children}
    </table>
  );
  if (responsive) return <div className="ds-table-responsive">{table}</div>;
  return table;
});

export const Badge = forwardRef(function Badge({ color, pill, tag: Tag = "span", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-badge", color && `ds-badge--${color}`, pill && "ds-badge--pill", className)} {...rest} />;
});

export const Alert = forwardRef(function Alert({ color = "primary", isOpen = true, toggle, className, children, ...rest }, ref) {
  if (!isOpen) return null;
  return (
    <div ref={ref} role="alert" className={cx("ds-alert", `ds-alert--${color}`, toggle && "ds-alert--dismissible", className)} {...rest}>
      {children}
      {toggle && <button type="button" className="ds-btn-close" aria-label="Close" onClick={toggle} />}
    </div>
  );
});

export const Progress = forwardRef(function Progress({ value = 0, max = 100, color, bar, className, children, ...rest }, ref) {
  const pct = `${(Number(value) / Number(max)) * 100}%`;
  if (bar) {
    return <div className={cx("ds-progress__bar", color && `ds-progress__bar--${color}`, className)} style={{ "--progress-width": pct }} {...rest}>{children}</div>;
  }
  return (
    <div ref={ref} className={cx("ds-progress", className)} {...rest}>
      <div className={cx("ds-progress__bar", color && `ds-progress__bar--${color}`)} style={{ "--progress-width": pct }}>{children}</div>
    </div>
  );
});

export const Spinner = forwardRef(function Spinner({ size, color, type = "border", className, children, ...rest }, ref) {
  return (
    <div ref={ref} role="status" className={cx("ds-spinner", type === "grow" && "ds-spinner--grow", size === "sm" && "ds-spinner--sm", color && `ds-spinner--${color}`, className)} {...rest}>
      {children || <span className="ds-visually-hidden">Loading...</span>}
    </div>
  );
});

// -----------------------------------------------------------------------------
// ListGroup
// -----------------------------------------------------------------------------
export const ListGroup = forwardRef(function ListGroup({ flush, tag: Tag = "ul", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-list", flush && "ds-list--flush", className)} {...rest} />;
});

export const ListGroupItem = forwardRef(function ListGroupItem({ active, disabled, action, color, tag: Tag = "li", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-list__item", active && "is-active", disabled && "is-disabled", action && "ds-list__item--action", color && `ds-list__item--${color}`, className)} {...rest} />;
});

export const ListGroupItemHeading = forwardRef(function ListGroupItemHeading({ tag: Tag = "h5", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-list__item-heading", className)} {...rest} />;
});
export const ListGroupItemText = forwardRef(function ListGroupItemText({ tag: Tag = "p", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-list__item-text", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Nav / Tabs
// -----------------------------------------------------------------------------
export const Nav = forwardRef(function Nav({ tabs, pills, vertical, justified, fill, tag: Tag = "ul", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-nav", tabs && "ds-nav--tabs", pills && "ds-nav--pills", vertical && "ds-nav--vertical", justified && "ds-nav--justified", fill && "ds-nav--fill", className)} {...rest} />;
});
export const NavItem = forwardRef(function NavItem({ tag: Tag = "li", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-nav__item", className)} {...rest} />;
});
export const NavLink = forwardRef(function NavLink({ active, disabled, tag: Tag = "a", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-nav__link", active && "is-active", disabled && "is-disabled", className)} {...rest} />;
});
export const TabContent = forwardRef(function TabContent({ activeTab, className, children, ...rest }, ref) {
  return (
    <TabContext.Provider value={activeTab}>
      <div ref={ref} className={cx("ds-tabs__content", className)} {...rest}>{children}</div>
    </TabContext.Provider>
  );
});
const TabContext = createContext(undefined);
export const TabPane = forwardRef(function TabPane({ tabId, className, children, ...rest }, ref) {
  const active = useContext(TabContext);
  const isActive = active !== undefined ? active === tabId : true;
  return <div ref={ref} className={cx("ds-tabs__pane", isActive && "is-active", className)} hidden={active !== undefined && !isActive} {...rest}>{children}</div>;
});

// -----------------------------------------------------------------------------
// Collapse (minimal — height auto/none)
// -----------------------------------------------------------------------------
export const Collapse = forwardRef(function Collapse({ isOpen, horizontal, navbar, tag: Tag = "div", className, children, ...rest }, ref) {
  return (
    <Tag ref={ref} className={cx("ds-collapse", isOpen ? "is-open" : "is-closed", className)} hidden={!isOpen && !navbar} {...rest}>
      {children}
    </Tag>
  );
});

// -----------------------------------------------------------------------------
// Card ailesi (Cuba .card yerine .ds-card — reactstrap Card kullananlar için)
// -----------------------------------------------------------------------------
export const Card = forwardRef(function Card({ color, body, tag: Tag = "div", className, children, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card", color && `ds-card--${color}`, body && "ds-card--body", className)} {...rest}>{children}</Tag>;
});
export const CardBody = forwardRef(function CardBody({ tag: Tag = "div", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card__body", className)} {...rest} />;
});
export const CardHeader = forwardRef(function CardHeader({ tag: Tag = "div", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card__header", className)} {...rest} />;
});
export const CardFooter = forwardRef(function CardFooter({ tag: Tag = "div", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card__footer", className)} {...rest} />;
});
export const CardTitle = forwardRef(function CardTitle({ tag: Tag = "h5", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card__title", className)} {...rest} />;
});
export const CardText = forwardRef(function CardText({ tag: Tag = "p", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-card__text", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Grid (Bootstrap row/col → .ds-row/.ds-col) — ince köprü; W5'te modüllere taşınır
// -----------------------------------------------------------------------------
const colClass = (key, val) => {
  if (val === undefined || val === null || val === false) return "";
  const infix = key === "xs" ? "" : `-${key}`;
  if (val === true) return `ds-col${infix}`;
  if (typeof val === "object") {
    return cx(val.size && `ds-col${infix}-${val.size}`, val.offset && `ds-col${infix}-offset-${val.offset}`, val.order && `ds-col${infix}-order-${val.order}`);
  }
  return `ds-col${infix}-${val}`;
};
export const Row = forwardRef(function Row({ tag: Tag = "div", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-row", className)} {...rest} />;
});
export const Col = forwardRef(function Col({ xs, sm, md, lg, xl, xxl, tag: Tag = "div", className, ...rest }, ref) {
  const cols = cx(colClass("xs", xs), colClass("sm", sm), colClass("md", md), colClass("lg", lg), colClass("xl", xl), colClass("xxl", xxl));
  return <Tag ref={ref} className={cx(cols || "ds-col", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Modal (gerçek davranış: portal + ESC + backdrop + scroll-lock)
// -----------------------------------------------------------------------------
// ---- Drawer resize (MERKEZÎ): tüm .ds-modal sağ-drawer'ları için tek mantık ----
// Sol kenardan sürükle → --ds-modal-width. resizeKey verilirse localStorage'da
// kalıcı (örn. inspector'lar paylaşır); yoksa o-açılış için geçici. Desktop-only
// (handle CSS'te mobilde gizli). min/max clamp.
const DRAWER_MIN_W = 360;
const DRAWER_MAX_W = 900;
const DRAWER_SIZE_W = { sm: 408, lg: 680, xl: 880 };
const _parseW = (v) => {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) ? n : null;
};
const _clampW = (w) =>
  Math.max(
    DRAWER_MIN_W,
    Math.min(DRAWER_MAX_W, Math.min(w, (typeof window !== "undefined" ? window.innerWidth : 1200) - 80))
  );

export const Modal = function Modal({
  isOpen, toggle, size, className, contentClassName, children, style, resizable = true, resizeKey,
  placement = "right",
  // ---- reactstrap <Modal> API parite props'ları — YUTULUR (native drawer
  // kendi davranışını uygular; bunlar DOM'a sızmasın diye burada tüketilir.
  // Eksik tüketim → "React does not recognize the X prop on a DOM element"). ----
  centered, scrollable, fade, backdrop, keyboard, autoFocus, role: _role,
  labelledBy, external, container, modalClassName, wrapClassName, backdropClassName,
  unmountOnClose, returnFocusAfterClose, trapFocus, zIndex, innerRef,
  fadeTransition, backdropTransition, modalTransition,
  onClosed, onOpened, onEnter, onEntered, onExit, onExiting, onExited,
  ...rest
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && toggle) toggle(e); };
    document.addEventListener("keydown", onKey);
    // Body scroll-lock: drawer açıkken sayfa scroll'u kilitlenir → çift scrollbar
    // (body + drawer) yok. Non-blocking korunur: overflow:hidden tıklamayı
    // engellemez, sadece scroll'u durdurur. (ref-count: iç içe modal güvenli.)
    document.body.dataset.dsModalCount = String((parseInt(document.body.dataset.dsModalCount, 10) || 0) + 1);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      const next = (parseInt(document.body.dataset.dsModalCount, 10) || 1) - 1;
      if (next <= 0) {
        delete document.body.dataset.dsModalCount;
        document.body.style.overflow = "";
      } else {
        document.body.dataset.dsModalCount = String(next);
      }
    };
  }, [isOpen, toggle]);

  // ---- Resize state (MERKEZÎ) ----
  const storageK = resizeKey ? `pms-drawer-w:${resizeKey}` : null;
  const defaultW = _parseW(style && style["--ds-modal-width"]) || DRAWER_SIZE_W[size] || 520;
  const [drawerW, setDrawerW] = useState(() => {
    if (storageK) {
      try {
        const v = localStorage.getItem(storageK);
        if (v) return _clampW(Number(v));
      } catch {
        /* yoksay */
      }
    }
    return defaultW;
  });
  const drawerWRef = useRef(drawerW);
  drawerWRef.current = drawerW;
  const [resizing, setResizing] = useState(false);

  const onResizeDown = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = drawerWRef.current;
      setResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      const move = (ev) => setDrawerW(_clampW(startW + (startX - ev.clientX)));
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        setResizing(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        if (storageK) {
          try {
            localStorage.setItem(storageK, String(drawerWRef.current));
          } catch {
            /* yoksay */
          }
        }
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [storageK]
  );

  const isCentered = placement === "center";
  const effectiveResizable = !isCentered && resizable;

  if (!isOpen) return null;
  // SADE drawer: ara dialog/content katmanı YOK — header/body/footer doğrudan
  // .ds-modal içinde (flex column). Animasyon .ds-modal'ın kendisinde (opacity+
  // 24px) → içerik-bağımsız, 3 drawer birebir aynı. isOpen false→null = fresh
  // mount → her açılışta animasyon. Non-blocking, backdrop yok, kapatma X/Esc.
  // resizable (default true) → sol kenarda MERKEZÎ resize tutamacı.
  //
  // placement === 'center': orta modal, blocking backdrop, resize yok, scale+fade
  // animasyon. right drawer davranışı değişmez.
  const centerWidth = style && style["--ds-modal-width"]
    ? style["--ds-modal-width"]
    : DRAWER_SIZE_W[size] || 520;
  const mergedStyle = effectiveResizable
    ? { ...style, "--ds-modal-width": `${drawerW}px` }
    : isCentered
      ? { ...style, "--ds-modal-width": typeof centerWidth === "number" ? `${centerWidth}px` : centerWidth }
      : style;

  const modalNode = (
    <div
      className={cx(
        "ds-modal",
        "is-open",
        size && `ds-modal--${size}`,
        isCentered && "ds-modal--centered",
        className,
        contentClassName,
        resizing && "is-resizing"
      )}
      role="dialog"
      style={mergedStyle}
      {...rest}
    >
      {effectiveResizable && (
        <div
          className="ds-modal__resize-handle"
          onPointerDown={onResizeDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Paneli yeniden boyutlandır"
          title="Sürükleyerek genişlet/daralt"
        />
      )}
      {children}
    </div>
  );

  return createPortal(
    isCentered ? (
      <>
        <div className="ds-modal-backdrop" onClick={toggle} aria-hidden="true" />
        {modalNode}
      </>
    ) : (
      modalNode
    ),
    document.body
  );
};

export const ModalHeader = function ModalHeader({ toggle, tag: Tag = "h5", className, children, close, ...rest }) {
  return (
    <div className={cx("ds-modal__header", className)} {...rest}>
      <Tag className="ds-modal__title">{children}</Tag>
      {close || (toggle && <button type="button" className="ds-btn-close" aria-label="Close" onClick={toggle} />)}
    </div>
  );
};
export const ModalBody = function ModalBody({ className, ...rest }) {
  return <div className={cx("ds-modal__body", className)} {...rest} />;
};
export const ModalFooter = function ModalFooter({ className, ...rest }) {
  return <div className={cx("ds-modal__footer", className)} {...rest} />;
};

// -----------------------------------------------------------------------------
// Dropdown (composition + context; controlled isOpen/toggle veya uncontrolled)
// -----------------------------------------------------------------------------
const DropdownContext = createContext(null);

function DropdownBase({ isOpen: controlledOpen, toggle: controlledToggle, direction, group, nav, inNavbar, setActiveFromChild, a11y, active, menuRole, tag: Tag = "div", className, children, ...rest }) {
  const uncontrolled = controlledOpen === undefined;
  const [open, setOpen] = useState(false);
  const isOpen = uncontrolled ? open : controlledOpen;
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const toggle = (e) => {
    if (uncontrolled) setOpen((v) => !v);
    else if (controlledToggle) controlledToggle(e);
  };
  const close = (e) => {
    if (uncontrolled) setOpen(false);
    else if (controlledToggle && controlledOpen) controlledToggle(e);
  };
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if ((t && t.contains(e.target)) || (m && m.contains(e.target))) return;
      close(e);
    };
    const onKey = (e) => { if (e.key === "Escape") close(e); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  return (
    <DropdownContext.Provider value={{ isOpen, toggle, direction, triggerRef, menuRef }}>
      <Tag ref={triggerRef} className={cx("ds-dropdown", direction && `ds-dropdown--${direction}`, isOpen && "is-open", className)} {...rest}>
        {children}
      </Tag>
    </DropdownContext.Provider>
  );
}
export const Dropdown = DropdownBase;
export const UncontrolledDropdown = function UncontrolledDropdown(props) {
  const { isOpen, toggle, ...rest } = props;
  return <DropdownBase {...rest} />;
};
export const ButtonDropdown = DropdownBase;
export const UncontrolledButtonDropdown = UncontrolledDropdown;

export const DropdownToggle = forwardRef(function DropdownToggle(
  { caret, color, outline, size, nav, tag, className, children, onClick, ...rest },
  ref
) {
  const ctx = useContext(DropdownContext) || {};
  const handle = (e) => { ctx.toggle && ctx.toggle(e); onClick && onClick(e); };
  const Tag = tag || (nav ? "a" : "button");
  const isBtn = Tag === "button";
  return (
    <Tag
      ref={ref}
      type={isBtn ? "button" : undefined}
      className={cx(
        nav ? "ds-nav__link" : "ds-btn",
        !nav && variant("ds-btn", color, outline),
        !nav && size === "sm" && "ds-btn--sm",
        !nav && size === "lg" && "ds-btn--lg",
        "ds-dropdown__toggle",
        // reactstrap default caret=false → ok varsayılan KAPALI; sadece caret={true}'da göster.
        caret !== true && "ds-dropdown__toggle--no-caret",
        ctx.isOpen && "is-open",
        className
      )}
      aria-expanded={!!ctx.isOpen}
      onClick={handle}
      {...rest}
    >
      {children}
    </Tag>
  );
});

// DropdownMenu — body'ye PORTAL + fixed konum (tablo/scroll overflow'unu aşar;
// reactstrap'in Popper davranışının yerine). Trigger rect'ine göre alta açılır,
// yer yoksa yukarı flip; end/right ise sağ kenara hizalı; viewport'a clamp.
export const DropdownMenu = forwardRef(function DropdownMenu({ end, right, start, flip, container, modifiers, persist, className, style, children, ...rest }, _ref) {
  const ctx = useContext(DropdownContext) || {};
  const localRef = useRef(null);
  const [pos, setPos] = useState(null);
  const alignEnd = !!(end || right);

  useLayoutEffect(() => {
    if (!ctx.isOpen || !ctx.triggerRef || !ctx.triggerRef.current) return undefined;
    const update = () => {
      const trig = ctx.triggerRef.current;
      const menu = localRef.current;
      if (!trig || !menu) return;
      const tr = trig.getBoundingClientRect();
      const mw = menu.offsetWidth || 180;
      const mh = menu.offsetHeight || 0;
      let left = alignEnd ? tr.right - mw : tr.left;
      left = Math.max(8, Math.min(left, window.innerWidth - mw - 8));
      let top = tr.bottom + 4;
      if (top + mh > window.innerHeight - 8 && tr.top - mh - 4 > 8) top = tr.top - mh - 4;
      setPos({ top, left });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [ctx.isOpen, alignEnd]);

  if (!ctx.isOpen) return null;
  const setRefs = (el) => {
    localRef.current = el;
    if (ctx.menuRef) ctx.menuRef.current = el;
  };
  return createPortal(
    <div
      ref={setRefs}
      className={cx("ds-dropdown__menu", "is-open", alignEnd && "ds-dropdown__menu--end", start && "ds-dropdown__menu--start", className)}
      style={{ "--menu-top": pos ? `${pos.top}px` : "-9999px", "--menu-left": pos ? `${pos.left}px` : "-9999px", ...style }}
      {...rest}
    >
      {children}
    </div>,
    document.body
  );
});

export const DropdownItem = forwardRef(function DropdownItem(
  { divider, header, text, disabled, active, toggle = true, tag, className, onClick, children, ...rest },
  ref
) {
  const ctx = useContext(DropdownContext) || {};
  if (divider) return <div className={cx("ds-dropdown__divider", className)} />;
  if (header) return <h6 className={cx("ds-dropdown__header", className)} {...rest}>{children}</h6>;
  if (text) return <span className={cx("ds-dropdown__item-text", className)} {...rest}>{children}</span>;
  const Tag = tag || "button";
  const handle = (e) => {
    if (disabled) { e.preventDefault(); return; }
    onClick && onClick(e);
    if (toggle && ctx.toggle) ctx.toggle(e);
  };
  return (
    <Tag
      ref={ref}
      type={Tag === "button" ? "button" : undefined}
      className={cx("ds-dropdown__item", active && "is-active", disabled && "is-disabled", className)}
      onClick={handle}
      {...rest}
    >
      {children}
    </Tag>
  );
});

// -----------------------------------------------------------------------------
// Tooltip / Popover (minimal — hover/controlled görünürlük, native title fallback)
// -----------------------------------------------------------------------------
export const Tooltip = function Tooltip({ isOpen, target, toggle, placement = "top", className, children, ...rest }) {
  if (!isOpen) return null;
  return <div role="tooltip" className={cx("ds-tooltip", `ds-tooltip--${placement}`, className)} {...rest}>{children}</div>;
};
export const UncontrolledTooltip = function UncontrolledTooltip({ target, placement = "top", className, children, ...rest }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const id = typeof target === "string" ? target.replace(/^#/, "") : null;
    const el = id ? document.getElementById(id) : null;
    if (!el) return undefined;
    const on = () => setOpen(true);
    const off = () => setOpen(false);
    el.addEventListener("mouseenter", on);
    el.addEventListener("mouseleave", off);
    el.addEventListener("focus", on);
    el.addEventListener("blur", off);
    return () => {
      el.removeEventListener("mouseenter", on);
      el.removeEventListener("mouseleave", off);
      el.removeEventListener("focus", on);
      el.removeEventListener("blur", off);
    };
  }, [target]);
  if (!open) return null;
  return <div role="tooltip" className={cx("ds-tooltip", `ds-tooltip--${placement}`, className)} {...rest}>{children}</div>;
};
export const Popover = function Popover({ isOpen, placement = "top", className, children, ...rest }) {
  if (!isOpen) return null;
  return <div className={cx("ds-popover", `ds-popover--${placement}`, className)} {...rest}>{children}</div>;
};
export const PopoverHeader = ({ className, ...rest }) => <div className={cx("ds-popover__header", className)} {...rest} />;
export const PopoverBody = ({ className, ...rest }) => <div className={cx("ds-popover__body", className)} {...rest} />;

// -----------------------------------------------------------------------------
// Media (deprecated BS pattern) / Pagination / Breadcrumb
// -----------------------------------------------------------------------------
export const Media = forwardRef(function Media({ body, object, list, heading, middle, bottom, tag, className, ...rest }, ref) {
  const Tag = tag || (heading ? "h4" : object ? "img" : list ? "ul" : "div");
  return <Tag ref={ref} className={cx(body ? "ds-media__body" : "ds-media", className)} {...rest} />;
});
export const Pagination = forwardRef(function Pagination({ size, listTag: Tag = "ul", "aria-label": al, className, ...rest }, ref) {
  return <nav aria-label={al}><Tag ref={ref} className={cx("ds-pagination", size && `ds-pagination--${size}`, className)} {...rest} /></nav>;
});
export const PaginationItem = forwardRef(function PaginationItem({ active, disabled, className, ...rest }, ref) {
  return <li ref={ref} className={cx("ds-pagination__item", active && "is-active", disabled && "is-disabled", className)} {...rest} />;
});
export const PaginationLink = forwardRef(function PaginationLink({ next, previous, first, last, tag: Tag = "a", className, children, ...rest }, ref) {
  const sym = previous ? "‹" : next ? "›" : first ? "«" : last ? "»" : null;
  return <Tag ref={ref} className={cx("ds-pagination__link", className)} {...rest}>{children || sym}</Tag>;
});
export const Breadcrumb = forwardRef(function Breadcrumb({ tag: Tag = "nav", listTag: ListTag = "ol", className, children, ...rest }, ref) {
  return <Tag ref={ref} aria-label="breadcrumb" {...rest}><ListTag className={cx("ds-breadcrumb", className)}>{children}</ListTag></Tag>;
});
export const BreadcrumbItem = forwardRef(function BreadcrumbItem({ active, tag: Tag = "li", className, ...rest }, ref) {
  return <Tag ref={ref} className={cx("ds-breadcrumb__item", active && "is-active", className)} {...rest} />;
});

// -----------------------------------------------------------------------------
// Carousel (davranışsal: aktif slide + auto-advance + prev/next)
// -----------------------------------------------------------------------------
export const CarouselItem = forwardRef(function CarouselItem({ active, onExiting, onExited, className, children, ...rest }, ref) {
  return <div ref={ref} className={cx("ds-carousel__item", active && "is-active", className)} {...rest}>{children}</div>;
});

export const Carousel = function Carousel({ activeIndex = 0, next, previous, interval = 5000, ride, keyboard, pause, className, children, ...rest }) {
  useEffect(() => {
    if (interval === false || interval === null || interval === undefined || typeof next !== "function") return undefined;
    const ms = typeof interval === "number" ? interval : 5000;
    const id = setInterval(() => next(), ms);
    return () => clearInterval(id);
  }, [interval, next, activeIndex]);

  const items = [];
  const others = [];
  React.Children.forEach(children, (ch) => {
    if (!ch) return;
    if (ch.type === CarouselItem) items.push(ch);
    else others.push(ch);
  });
  return (
    <div className={cx("ds-carousel", className)} {...rest}>
      <div className="ds-carousel__inner">
        {items.map((it, i) => React.cloneElement(it, { active: i === activeIndex, key: it.key ?? i }))}
      </div>
      {others}
    </div>
  );
};

export const CarouselControl = function CarouselControl({ direction, onClickHandler, directionText, className, ...rest }) {
  return (
    <button
      type="button"
      className={cx("ds-carousel__control", `ds-carousel__control--${direction}`, className)}
      onClick={onClickHandler}
      aria-label={directionText || direction}
      {...rest}
    >
      <span className={cx("ds-carousel__control-icon", `ds-carousel__control-icon--${direction}`)} aria-hidden="true" />
    </button>
  );
};

export const CarouselIndicators = function CarouselIndicators({ items = [], activeIndex, onClickHandler, className, ...rest }) {
  return (
    <ol className={cx("ds-carousel__indicators", className)} {...rest}>
      {items.map((it, i) => (
        <li key={it.key || i} className={cx("ds-carousel__indicator", i === activeIndex && "is-active")} onClick={() => onClickHandler && onClickHandler(i)} />
      ))}
    </ol>
  );
};

export const CarouselCaption = function CarouselCaption({ captionHeader, captionText, className, ...rest }) {
  return (
    <div className={cx("ds-carousel__caption", className)} {...rest}>
      <h3>{captionHeader}</h3>
      <p>{captionText}</p>
    </div>
  );
};
